import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import CustomNotify from '../../../Notify';
import PostbackParametersComponent from '../PostbackParameters/PostbackParameters.container';
import PostbackGoalActionsComponent from '../PostbackGoalActions/PostbackGoalActions.container';
import PostbackTestComponent from '../PostbackTest/PostbackTest.container';
import FormGroup from '../../../FormGroup';
import ConversionStatuses from '../ConversionStatuses';
import ViewLink from '../ViewLink';
import TrackParameters from '../TrackParameters';
import SuccessString from '../SuccessString';
import ButtonsList from '../../../ButtonsList';
import Accordion from "../../../UI/blocks/Accordion";
import { getPath } from '../../../../utils/Router';
import { useTemplateConfig } from '../../../../context/TemplateConfigContext';

const sanitizeParameters = (params = []) => {
    const seen = new Set();
    return Array.isArray(params)
        ? params.filter((item = {}) => {
            const name = item?.name;
            const value = item?.value;
            if (!name || !value) return false;
            const key = `${name}:::${value}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        : [];
};

const normalizeLocale = (value, availableLocales = []) => {
    const localesList = Array.isArray(availableLocales) && availableLocales.length
        ? availableLocales
        : [];
    const fallback = localesList?.[0]?.value || 'en';
    if (!value) {
        return fallback;
    }
    const normalizedValue = String(value);
    const short = normalizedValue.split('_')[0];
    const matchShort = localesList.find(locale => locale.value === short);
    if (matchShort) {
        return matchShort.value;
    }
    const matchExact = localesList.find(locale => locale.value === normalizedValue);
    if (matchExact) {
        return matchExact.value;
    }
    return fallback;
};

const PostbackPage = ({ 
    postback, 
    params, 
    user, 
    errors, 
    loadingSave, 
    saveSuccess,
    newCreateId,
    currencies, 
    locales, 
    onSave, 
    clearErrors,
    onDelete,
    history,
    t 
}) => {
    const { assets } = useTemplateConfig();
    const isEditMode = !!params?.id;
    const initialPostback = isEditMode ? (postback || {}) : {};
    const normalizeErrors = (errs) => {
        const normalized = {};
        if (errs && typeof errs === 'object') {
            Object.keys(errs).forEach(key => {
                if (Array.isArray(errs[key])) {
                    normalized[key] = errs[key].filter(e => e);
                } else if (errs[key]) {
                    normalized[key] = [String(errs[key])];
                } else {
                    normalized[key] = [];
                }
            });
        }
        return normalized;
    };

    const localeOptions = useMemo(() => {
        const sourceLocales = Array.isArray(locales) && locales.length
            ? locales
            : (assets?.locales || []);
        return (sourceLocales || []).map(locale => ({
            value: locale.value || locale.key,
            title: locale.title || locale.name || locale.value || locale.key
        }));
    }, [locales, assets]);

    const initialLocale = normalizeLocale(initialPostback.locale || user?.locale, localeOptions);

    const getCreateDefaults = useCallback(() => ({
        id: null,
        edit: false,
        title: '',
        url: '',
        queryParams: [],
        method: 'GET',
        trackParameter: {},
        successString: '',
        conversionStatuses: ['confirmed'],
        currency: user?.publisher?.account?.balance?.currency || 'USD',
        locale: normalizeLocale(user?.locale, localeOptions),
        errors: {},
        loadingSave: loadingSave,
        goalActionIds: [],
        formErrorMessage: ''
    }), [user, localeOptions, loadingSave]);

    const [state, setState] = useState(() => {
        if (!isEditMode) {
            return getCreateDefaults();
        }

        return {
            id: params?.id || null,
            edit: true,
            title: initialPostback.title || '',
            url: initialPostback.url || '',
            queryParams: (Array.isArray(initialPostback.parameters)) ? sanitizeParameters(initialPostback.parameters) : [],
            method: initialPostback.method || 'GET',
            trackParameter: initialPostback.track_parameter || {},
            successString: initialPostback.success_string || '',
            conversionStatuses: initialPostback.conversion_statuses || [],
            currency: initialPostback.currency || user?.publisher?.account?.balance?.currency,
            locale: initialLocale,
            errors: normalizeErrors(errors),
            loadingSave: loadingSave,
            goalActionIds: initialPostback.goal_action_ids || [],
            formErrorMessage: ''
        };
    });

    const validateParametersRef = useRef(null);
    const queryParamsRef = useRef(state.queryParams || []);
    const shouldFocusErrorRef = useRef(false);
    const prevPostbackRef = useRef(null);

    const staticNames = (t && t.postbackPage && t.postbackPage.creatingEditing) || {};

    const onShowError = useCallback((message) => {
        const errorMessage = message || staticNames.commonError || 'Error occurred';
        CustomNotify(errorMessage, 'error', { toastId: `error-${Date.now()}` });
    }, [staticNames.commonError]);

    const onChangeField = useCallback((event) => {
        const name = event.target.name;
        let value = event.target.value;

        setState(prevState => {
            const newState = {
                ...prevState,
                [name]: value
            };
            
            if (prevState.errors[name]) {
                newState.errors = {
                    ...prevState.errors,
                    [name]: []
                };
            }
            
            return newState;
        });
    }, []);

    const onChangeDropdownList = useCallback((item, type) => {
        setState(prevState => ({
            ...prevState,
            [type]: item.value
        }));
    }, []);

    const onParametersAction = useCallback((actionState) => {
        const sanitized = sanitizeParameters(actionState.selected);
        queryParamsRef.current = sanitized;
        setState(prevState => ({
            ...prevState,
            queryParams: sanitized
        }));
    }, []);

    const onGoalAction = useCallback((ids) => {
        setState(prevState => ({
            ...prevState,
            goalActionIds: ids
        }));
    }, []);

    const onConversionStatusesAction = useCallback((actionState) => {
        setState(prevState => ({
            ...prevState,
            conversionStatuses: actionState.statuses
        }));
    }, []);

    const onChangeLocale = useCallback((value) => {
        setState(prevState => ({
            ...prevState,
            locale: normalizeLocale(value, localeOptions)
        }));
    }, [localeOptions]);

    const onChangeCurrency = useCallback((value) => {
        setState(prevState => ({
            ...prevState,
            currency: value
        }));
    }, []);

    const onTrackParameterAction = useCallback((actionState) => {
        setState(prevState => ({
            ...prevState,
            trackParameter: {
                ...actionState
            }
        }));
    }, []);

    const onSuccessString = useCallback((text) => {
        setState(prevState => ({
            ...prevState,
            successString: text
        }));
    }, []);

    const validateForm = useCallback((currentState) => {
        const stateToValidate = currentState || state;
        const newErrors = {};
        const staticNames = (t && t.postbackPage && t.postbackPage.creatingEditing) || {};
        
        if (!stateToValidate.title || !stateToValidate.title.trim()) {
            newErrors.title = [staticNames.form?.nameRequired || 'Name/title is required'];
        }
        
        if (!stateToValidate.url || !stateToValidate.url.trim()) {
            newErrors.url = [staticNames.form?.urlRequired || 'Postback URL is required'];
        }
        
        if (validateParametersRef.current) {
            const validationResult = validateParametersRef.current();
            if (!validationResult.isValid) {
                newErrors.parameters = [staticNames.parameterError || 'Please fill in both name and value for all parameters'];
            }
        }
        
        return newErrors;
    }, [state, t]);

    const onSubmit = useCallback((event) => {
        event.preventDefault();

        const currentState = {
            ...state,
            queryParams: queryParamsRef.current
        };
        const validationErrors = validateForm(currentState);
        const hasErrors = Object.keys(validationErrors).length > 0;

        if (hasErrors) {
            shouldFocusErrorRef.current = true;
            const errorMessages = Object.values(validationErrors).flat();
            const validationMessage = errorMessages.length > 0
                ? errorMessages.join(', ')
                : (staticNames.commonError || 'Please fill in all required fields');

            setState(prevState => ({
                ...prevState,
                errors: validationErrors,
                formErrorMessage: validationMessage
            }));

            setTimeout(() => {
                onShowError(validationMessage);
            }, 0);

            return;
        }

        setState(prevState => ({
            ...prevState,
            formErrorMessage: ''
        }));

        setTimeout(() => {
            onSave(currentState);
        }, 0);
    }, [state, onSave, validateForm, staticNames, onShowError]);

    const getListFields = useCallback((list, name, curValue) => {
        return list.map((item) => (
            <label className="checkbox-inline i-checks option" key={item.value}>
                <input
                    className="option__input"
                    type="radio"
                    name={name}
                    value={item.value}
                    onChange={onChangeField}
                    checked={curValue === item.value}
                />
                <span className="option__mark" />
                <span className="option__caption">{item.title}</span>
            </label>
        ));
    }, [onChangeField]);

    const getForm = useCallback(() => {
        return (
            <div className="top-form">
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.title || []}
                >
                    <label className="bold"> 
                        <div> {staticNames.form?.name}* </div>
                        <input
                            className={`form-control ${(!state.title || !state.title.trim()) && (state.errors.title?.length > 0) ? 'error-field' : ''}`}
                            type="text"
                            name="title"
                            value={state.title}
                            onChange={onChangeField}
                            required={true}
                        />
                    </label>
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.url || []}
                >
                     <label className="bold"> 
                        <div> {staticNames.form?.url}* </div>
                        <input
                            className={`form-control ${(!state.url || !state.url.trim()) && (state.errors.url?.length > 0) ? 'error-field' : ''}`}
                            type="text"
                            name="url"
                            value={state.url}
                            onChange={onChangeField}
                            required={true}
                        />
                    </label>
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.currency || []}
                >
                    <ButtonsList 
                        fields={[{key: 'currencies', list: currencies, label: staticNames.form?.currency}]} 
                        data={{key: 'currencies'}}
                        onChange={onChangeCurrency}
                        currentValue={state.currency}
                    />
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.locale || []}
                >
                    <ButtonsList 
                        fields={[{key: 'locale', list: localeOptions, label: staticNames.form?.locale}]} 
                        data={{key: 'locale'}}
                        onChange={onChangeLocale}
                        currentValue={state.locale}
                    />
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.method || []}
                >
                    <label className="bold"> 
                        {staticNames.form?.method}
                        {getListFields([
                            {value: 'GET', title: 'GET'},
                            {value: 'POST', title: 'POST'}
                        ], 'method', state.method)}
                    </label>
                </FormGroup>
                <FormGroup
                    fieldClass="input-wrapper"
                    errors={state.errors.successString || []}
                >
                    <label className="bold">
                        {staticNames.form?.successString}
                        <SuccessString
                            text={state.successString}
                            onAction={onSuccessString}
                        />
                    </label>
                </FormGroup>
            </div>
        );
    }, [state, staticNames, currencies, locales, onChangeField, onChangeCurrency, onChangeLocale, onSuccessString, getListFields]);

    useEffect(() => {
        const normalizedErrors = {};
        if (errors && typeof errors === 'object') {
            Object.keys(errors).forEach(key => {
                if (Array.isArray(errors[key])) {
                    normalizedErrors[key] = errors[key].filter(e => e);
                } else if (errors[key]) {
                    normalizedErrors[key] = [String(errors[key])];
                } else {
                    normalizedErrors[key] = [];
                }
            });
        }

        const hasServerErrors = Object.keys(normalizedErrors).length > 0 && loadingSave === false;

        setState(prevState => ({
            ...prevState,
            errors: normalizedErrors,
            loadingSave: loadingSave
        }));

        if (hasServerErrors) {
            onShowError();
        }
    }, [errors, loadingSave, onShowError]);

    useEffect(() => {
        queryParamsRef.current = state.queryParams || [];
    }, [state.queryParams]);

    useEffect(() => {
        if (!params?.id) {
            prevPostbackRef.current = null;
            return;
        }

        if (!postback) {
            return;
        }

        const hasData = postback.title !== undefined || postback.url !== undefined || postback.method !== undefined;
        if (!hasData) {
            return;
        }

        const postbackChanged = prevPostbackRef.current !== postback;
        if (!postbackChanged && prevPostbackRef.current) {
            return;
        }

        prevPostbackRef.current = postback;

        setState(prevState => ({
            ...prevState,
            title: postback.title !== undefined ? postback.title : prevState.title,
            url: postback.url !== undefined ? postback.url : prevState.url,
            method: postback.method !== undefined ? postback.method : prevState.method,
            currency: postback.currency !== undefined ? postback.currency : prevState.currency,
            locale: postback.locale !== undefined ? normalizeLocale(postback.locale, localeOptions) : prevState.locale,
            trackParameter: postback.track_parameter !== undefined ? (postback.track_parameter || {}) : prevState.trackParameter,
            successString: postback.success_string !== undefined ? postback.success_string : prevState.successString,
            conversionStatuses: Array.isArray(postback.conversion_statuses) ? postback.conversion_statuses : prevState.conversionStatuses,
            goalActionIds: Array.isArray(postback.goal_action_ids) ? postback.goal_action_ids : prevState.goalActionIds,
            queryParams: Array.isArray(postback.parameters) ? sanitizeParameters(postback.parameters) : prevState.queryParams
        }));

        queryParamsRef.current = Array.isArray(postback.parameters)
            ? sanitizeParameters(postback.parameters)
            : queryParamsRef.current;
    }, [postback, params?.id, localeOptions]);

    useEffect(() => {
        if (!params?.id && (state.edit || state.id)) {
            prevPostbackRef.current = null;
            setState(getCreateDefaults());
        }
    }, [params?.id, state.edit, state.id, getCreateDefaults]);

    useEffect(() => {
        if (saveSuccess && newCreateId) {
            setState(prevState => {
                if (!prevState.id && !prevState.edit) {
                    return {
                        ...prevState,
                        id: newCreateId,
                        edit: true
                    };
                }
                return prevState;
            });

            if (!params?.id) {
                const editPath = getPath('editPostback', { id: newCreateId });
                if (history?.push) {
                    history.push(editPath);
                } else if (history?.replace) {
                    history.replace(editPath);
                }
            }
        }
    }, [saveSuccess, newCreateId, history, params?.id]);

    useEffect(() => {
        const paramsId = params?.id ? String(params.id) : null;
        const stateId = state.id ? String(state.id) : null;
        
        if (paramsId && paramsId !== stateId) {
            setState(prevState => ({
                ...prevState,
                id: params.id,
                edit: true
            }));
        } else if (!paramsId && state.edit && !stateId) {
            setState(prevState => ({
                ...prevState,
                id: null,
                edit: false
            }));
        }
    }, [params?.id, state.id, state.edit]);

    useEffect(() => {
        return () => {
            clearErrors();
        };
    }, [clearErrors]);

    useEffect(() => {
        if (typeof document === 'undefined' || !state.formErrorMessage || !shouldFocusErrorRef.current) {
            return;
        }

        const firstErrorKey = Object.keys(state.errors || {}).find((key) => {
            const fieldErrors = state.errors[key];
            return Array.isArray(fieldErrors) && fieldErrors.length;
        });

        if (!firstErrorKey || firstErrorKey === 'parameters') {
            shouldFocusErrorRef.current = false;
            return;
        }

        const errorField = document.querySelector(`[name="${firstErrorKey}"]`);
        if (errorField) {
            errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorField.focus();
        }
        shouldFocusErrorRef.current = false;
    }, [state.formErrorMessage, state.errors]);


    return (
        <div className="ibox-content">
            <div className="postback__add-block">
                <form className="postback__add-left" onSubmit={onSubmit} noValidate={true}>
                    <Accordion title={staticNames.accordionMainSettings}>
                        {getForm()}
                    </Accordion>

                    <Accordion title={staticNames.accordionGoals}>
                    <div className="postback__filter">
                        <TrackParameters
                            data={state.trackParameter}
                            onAction={onTrackParameterAction}
                        />
                        <ConversionStatuses
                            onAction={onConversionStatusesAction}
                            type={state.edit}
                            statuses={state.conversionStatuses}
                        />

                        <PostbackGoalActionsComponent
                            onAction={onGoalAction}
                        />
                    </div>
                    </Accordion>

                    <Accordion title={staticNames.accordionParams}>
                    <PostbackParametersComponent
                        onAction={onParametersAction}
                        errors={state.errors.parameters}
                        onValidate={(validateFn) => {
                            validateParametersRef.current = validateFn;
                        }}
                    />

                    <ViewLink
                        urlPostback={state.url}
                        gethParams={state.queryParams}
                    />

                    <PostbackTestComponent
                        edit={state.edit}
                        id={state.id}
                    />
                    </Accordion>
                </form>
            </div>
        </div>
    );
};

PostbackPage.propTypes = {
    postback: PropTypes.shape({
        conversion_statuses: PropTypes.array,
        currency: PropTypes.string,
        goal_action_ids: PropTypes.array,
        locale: PropTypes.string,
        method: PropTypes.string,
        parameters: PropTypes.array,
        title: PropTypes.string,
        url: PropTypes.string,
        track_parameter: PropTypes.object,
        success_string: PropTypes.string
    }),
    params: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    user: PropTypes.shape({
        locale: PropTypes.string,
        publisher: PropTypes.shape({
            account: PropTypes.shape({
                balance: PropTypes.shape({
                    currency: PropTypes.string
                })
            })
        })
    }),
    errors: PropTypes.object,
    loadingSave: PropTypes.bool,
    saveSuccess: PropTypes.bool,
    newCreateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currencies: PropTypes.array,
    locales: PropTypes.array,
    onSave: PropTypes.func,
    clearErrors: PropTypes.func,
    onDelete: PropTypes.func,
    history: PropTypes.object,
    t: PropTypes.object
};

PostbackPage.defaultProps = {
    postback: {
        conversion_statuses: [],
        currency: '',
        goal_action_ids: [],
        locale: '',
        method: 'GET',
        parameters: [],
        title: '',
        url: '',
        track_parameter: {},
        success_string: ''
    },
    params: {},
    user: {},
    errors: {},
    loadingSave: false,
    currencies: [],
    locales: [],
    onSave: () => {},
    clearErrors: () => {},
    onDelete: null,
    history: null
};

export default withTranslation(PostbackPage);
